import React, { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useReportsVocabApi } from '@nautilusjs/mati-apis'
import { WEEKDAYS, TIMEZONE_LOCATIONS } from '@/utilities'
import { useNotifications } from '../useNotifications'
import { BaseFormContent, LayoutColumn, Label } from '../common'
import { DropdownCheckboxList } from '../common/fields'
import { Button, Loading, ModalFooter } from '@maui/elements'

export const ReportTypeDropdown = ({ control }) => {
  const { data: reportsVocab } = useReportsVocabApi()

  const reportTypes = useMemo(() => {
    if (!reportsVocab) return
    const { report_types: reportTypes } = reportsVocab
    const formattedReportTypes = reportTypes.map(type => {
      // The Vul Report should not be displayed now that the
      // Vulns have their own digest
      return !type.includes('Vulnerability Report') && { label: type, value: type }
    }).filter(item => item)
    // Add the Patch Report
    formattedReportTypes.push({ label: 'Patch Report', value: 'Patch Report' })
    formattedReportTypes.sort((a, b) => a.label.localeCompare(b.label))
    return formattedReportTypes
  }, [reportsVocab])

  return (
    <LayoutColumn gap='spaceGapSmall' mb='spaceGapMedium'>
      <Label htmlFor='reportTypes'>
        Report Type
      </Label>
      <Controller
        control={control}
        name='reportTypes'
        rules={{ required: true }}
        render={(control) => {
          return (
            <DropdownCheckboxList
              control={control}
              options={reportTypes}
              name='reportTypes'
              placeholder='Please Select a Report Type'
            />
          )
        }}
      />
    </LayoutColumn>
  )
}

export const ReportForm = ({
  user,
  onClose,
  editData: data,
}) => {
  const { onSubmit, isSubmitting, error } = useNotifications({ onClose, data })
  const { days, startTimes } = data?.schedules[0] || {}
  const deliveryFreq = (startTimes?.length === 24 && days?.length === 7) ? 'HOURLY' : 'DAILY'
  const defaultTime = data?.schedules[0].startTimes[0] ? '0' : '8'
  const timezoneValue = data?.schedules[0].location
  const timezoneValid = data ? TIMEZONE_LOCATIONS.includes(timezoneValue) : true
  const localTimezoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone

  const { handleSubmit, control, register, setError, clearErrors, setValue, formState: { errors, dirtyFields, isValid, isDirty } } = useForm({
    defaultValues: {
      name: data?.displayName || '',
      emailAddress: user.email,
      deliveryFreq,
      deliveryDays: data?.schedules[0].days.map(String) || WEEKDAYS.map(day => day.value),
      deliveryTime: data?.schedules[0].startTimes[0].hours?.toString() || defaultTime,
      timeZone: data ? timezoneValue : localTimezoneValue,
      reportTypes: data?.config?.mandiantReport?.reportType || ['All'],
      path: data?.name || '',
      type: 'MANDIANT_REPORT',
    }
  })

  useEffect(() => {
    if (error) {
      setError('root.serverError', {
        type: 'manual',
        message: error?.data?.detail || error.message
      })
    }
  }, [error, setError])

  // If the timezone is not in the dropdown list, set the field to the user's local timezone
  // and enable the save button. The user can choose a new one or submit the one provided.
  useEffect(() => {
    if (timezoneValid) return
    setValue('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone, { shouldDirty: true })
  }, [timezoneValid, setValue])

  return (
    <form key="ReportForm" onSubmit={handleSubmit(onSubmit(dirtyFields))}>
      <BaseFormContent
        control={control}
        register={register}
        deliveryFreq={deliveryFreq}
        errors={errors}
        clearErrors={clearErrors}
        setValue={setValue}
        DropdownComponent={ReportTypeDropdown}
        timezoneValid={timezoneValid}
      />
      <ModalFooter mt="spaceGapXxLarge" gap="spaceGapMedium">
        { isSubmitting && <Loading /> }
        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button marginLeft="spaceGapXLarge" type="submit" disabled={isSubmitting || !isValid || !isDirty}>Save</Button>
      </ModalFooter>
    </form>
  )
}
